import * as React from 'react';
import { ServiceSummaryOptionsType, ServiceSummaryType } from '../../ServiceSummary/types';
import { API_DATE_FORMAT, format, removeDotsFromDate } from '../../utils';
import ServiceSummary from '../../ServiceSummary';
import ServiceSummaryOption from '../../ServiceSummary/components/ServiceSummaryOption';
import { parse } from 'date-fns';
import { useContext } from 'react';
import { CartContext } from '../Cart';
import { useTranslation } from 'react-i18next';
import { CartServiceProps } from '../types';
import ServiceSummaryDivider from '../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import { CheckoutSteps } from '../../MobileStepbar/types';
import { CdnIcon, CdnIconNames } from '@shared/ui';
import { buildFareBreakdown } from '@entities/fare';
import { AviaPassengerFareFragment } from '@websky/graphql';
import { useConfig } from '../../context';
import { useShowTaxesInfo } from '../../hooks';

const FareIcons: { [key: string]: CdnIconNames } = {
	ADT: 'Info/Passengers',
	CNN: 'Tour/Child',
	INF: 'Baggage/Baby carriage',
	INS: 'Tour/Child'
};

const Flights: React.FC<CartServiceProps> = ({ flight, renderService, hideFlightPrice, activeStep, steps }) => {
	const cartContext = useContext(CartContext);

	const { t } = useTranslation('Cart');
	const { splitCartFlightPassengers } = useConfig().global;
	const showTaxesInfo = useShowTaxesInfo();

	const fareBreakdown = React.useMemo(
		() =>
			showTaxesInfo
				? buildFareBreakdown({
						passengerFares: (flight.fares ?? []).reduce<AviaPassengerFareFragment[]>(
							(acc, fare) => acc.concat(fare?.passengerFares ?? []),
							[]
						),
						groups: flight.segmentGroups.map(group => ({
							groupId: group.groupId,
							segmentIds: group.segments.map(segment => segment.id)
						})),
						splitPassengers: splitCartFlightPassengers
				  })
				: null,
		[flight, showTaxesInfo, splitCartFlightPassengers]
	);

	const groupOptions = React.useMemo(() => {
		const groupOptions: ServiceSummaryOptionsType[] = [];

		const passengerTypeCache = new Map<string, string>();
		const getPassengerTypeName = (type: string, count: number) => {
			const key = `${type}_${count}`;

			if (!passengerTypeCache.has(key)) {
				passengerTypeCache.set(key, t(type, { ns: 'PassengerTypes', count }));
			}

			return passengerTypeCache.get(key);
		};

		flight.segmentGroups.forEach(segment => {
			const firstSegment = segment.segments[0];
			const lastSegment = segment.segments[segment.segments.length - 1];

			const departureDate = parse(firstSegment.departure.date, API_DATE_FORMAT, new Date());
			const formattedDate = removeDotsFromDate(format(departureDate, 'dd MMM, EEEEEE'));

			groupOptions.push({
				header: `${firstSegment.departure.airport.city.name} — ${lastSegment.arrival.airport.city.name}`,
				headerIcon: <CdnIcon icon={'Flight/Direction'} />,
				items: [
					{
						id: 'date',
						name: `${formattedDate} • ${firstSegment.departure.time} - ${lastSegment.arrival.time}`
					},
					{
						id: 'fare',
						name: segment.fareFamily?.title,
						price: !hideFlightPrice ? segment.price : undefined
					}
				]
			});

			if (fareBreakdown) {
				const group = fareBreakdown.groups.find(item => item.groupId === segment.groupId);

				if (group) {
					const passengerNumbers = new Map<string, number>();

					group.passengers.forEach(passengerFare => {
						const currentNumber = passengerNumbers.get(passengerFare.passengerType) || 0;
						passengerNumbers.set(passengerFare.passengerType, currentNumber + 1);

						const items: typeof groupOptions[0]['items'] = [
							{
								id: 'baseFarePrice',
								name: t('Fare'),
								price: passengerFare.baseFare
							}
						];

						if (passengerFare.totalTaxes) {
							items.push({
								id: 'totalTaxesPrice',
								name: t('Tax'),
								price: passengerFare.totalTaxes
							});
						}

						const passengerTypeName = getPassengerTypeName(
							passengerFare.passengerType,
							passengerFare.count
						);

						const header = splitCartFlightPassengers
							? `${passengerTypeName} ${currentNumber + 1}`
							: `${passengerFare.count} ${passengerTypeName}`;

						const icon = FareIcons[passengerFare.passengerType] ?? FareIcons['ADT'];

						groupOptions.push({
							description: 'paxesDetails',
							id: `${segment.groupId}_${passengerFare.passengerType}`,
							header,
							headerIcon: <CdnIcon icon={icon} />,
							items
						});
					});
				}
			}
		});

		return groupOptions;
	}, [flight, hideFlightPrice, splitCartFlightPassengers, t, fareBreakdown]);

	const group: ServiceSummaryType = {
		header: t('Flights'),
		icon: <CdnIcon icon="Flight/Direction" />,
		totalPrice: !hideFlightPrice ? cartContext.totalPrices?.flights : undefined,
		disabled: activeStep < steps?.findIndex(step => step.type === CheckoutSteps.Flights) || false
	};

	const content = (
		<>
			<ServiceSummaryDivider>
				{groupOptions.map(groupOption => (
					<ServiceSummaryOption groupOption={groupOption} key={groupOption.id || groupOption.header} />
				))}
			</ServiceSummaryDivider>
		</>
	);

	if (!groupOptions.length) {
		return null;
	}

	if (renderService) {
		return renderService(CheckoutSteps.Flights, group, content);
	}

	return <ServiceSummary group={group}>{content}</ServiceSummary>;
};

export default Flights;
